import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ChevronUp, ScrollText } from 'lucide-react';

import DirectorLog, { classify } from '@/components/production/DirectorLog';
import type { ProductionState } from '@/hooks/useProduction';
import { cn } from '@/lib/utils';

const OPEN_KEY = 'se_log_open';
const DEFAULT_HEIGHT = 208;

/** Remembered across visits, so the panel doesn't fight whoever wants it open. */
function initialOpen(): boolean {
  try {
    return localStorage.getItem(OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * The Director's Log, docked to the bottom of the console and COLLAPSED by default.
 *
 * It is a raw engine trace — genuinely useful when a run misbehaves, and noise the
 * rest of the time. Left permanently open it consumed 208px and implied a level of
 * technical involvement most producers don't want.
 *
 * Collapsing something does risk hiding a real failure, so the closed bar still
 * reports how many error/warning lines are in there: the log is out of the way, but
 * a problem inside it is not.
 */
export default function ResizableLogPanel({ logs }: { logs: ProductionState['logs'] }) {
  const { t } = useTranslation('production');
  const [open, setOpen] = useState(initialOpen);
  const [height, setHeight] = useState(DEFAULT_HEIGHT);
  const isDragging = useRef(false);
  const startY = useRef(0);
  const startHeight = useRef(0);

  const issues = useMemo(() => logs.reduce((n, log) => (['error', 'warning'].includes(classify(log)) ? n + 1 : n), 0), [logs]);

  const toggle = () => {
    setOpen((o) => {
      try {
        localStorage.setItem(OPEN_KEY, o ? '0' : '1');
      } catch {
        /* no-storage — the panel just won't remember */
      }
      return !o;
    });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    startY.current = e.clientY;
    startHeight.current = height;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';

    const handleMouseMove = (ev: MouseEvent) => {
      if (!isDragging.current) return;
      const delta = startY.current - ev.clientY;
      setHeight(Math.max(100, Math.min(window.innerHeight * 0.7, startHeight.current + delta)));
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    // Collapsed the container paints nothing — the tab itself carries the border and
    // background, so it reads as a tab sitting on the bottom edge rather than a
    // full-width strip with content bunched at one end.
    <div className={cn('flex-shrink-0', open && 'border-t border-border bg-elevated')}>
      {/* Only draggable while open — a resize grip above a collapsed bar is a
          control for something you can't see. */}
      {open && (
        <div
          role="separator"
          aria-label={t('log.resize')}
          className="h-1.5 cursor-row-resize transition-colors hover:bg-primary/20 active:bg-primary/30"
          onMouseDown={handleMouseDown}
        >
          <div className="mx-auto mt-0.5 h-0.5 w-8 rounded bg-border" />
        </div>
      )}

      {/* Collapsed, this is a tab only as wide as its own content and anchored left,
          so the floating Director button (fixed bottom-right) has clear space and
          never covers the entry count or the expand chevron. Open, it becomes a
          normal full-width panel header — the chevron is then at the TOP of a 208px
          panel, nowhere near the button at the bottom. */}
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        title={open ? t('log.collapse') : t('log.expand')}
        className={cn(
          'flex items-center gap-2.5 py-2.5 text-left transition-colors hover:bg-foreground/[0.04]',
          open ? 'w-full px-6' : 'ml-6 w-auto rounded-t-lg border border-b-0 border-border bg-elevated px-4 py-2',
        )}
      >
        <ScrollText className="h-3.5 w-3.5 shrink-0 text-faint" />
        <span className="text-[10.5px] font-semibold uppercase tracking-wider text-faint">{t('log.title')}</span>

        {issues > 0 && (
          <span
            className="inline-flex items-center gap-1 rounded-full border border-destructive/30
                       bg-destructive/12 px-2 py-0.5 font-mono text-[10px] text-destructive"
          >
            <AlertTriangle className="h-3 w-3" />
            {t('log.issues', { count: issues })}
          </span>
        )}

        <span className={cn('font-mono text-[10.5px] text-faint', open && 'ml-auto')}>
          {t('log.entries', { count: logs.length })}
        </span>
        <ChevronUp className={cn('h-4 w-4 shrink-0 text-faint transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div style={{ height }}>
          <DirectorLog logs={logs} />
        </div>
      )}
    </div>
  );
}
